import { action, computed } from "mobx";

import { FieldType, ViewModelBase } from "@shoothill/core";
import { AppUrls } from "AppUrls";
import { formatCurrencyFromPounds, formatDateTime } from "Utils/Format";
import { MyApprovalsNewListItemModel } from "./MyApprovalsNewListItemModel";
import { ApprovalListViewModel } from "./ApprovalListViewModel";

export class MyApprovalsNewListItemViewModel extends ViewModelBase<MyApprovalsNewListItemModel> {
    // #region Constructors and Disposers

    constructor() {
        super(new MyApprovalsNewListItemModel());
    }

    // #endregion Constructors and Disposers

    // #region Properties

    @computed
    public get refNameFormatted(): string {
        if (this.model.reference && this.model.name) {
            return `${this.model.reference} - ${this.model.name}`;
        } else if (this.model.reference) {
            return `${this.model.reference}`;
        } else if (this.model.name) {
            return `${this.model.name}`;
        }

        return ``;
    }

    @computed
    public get requestedOnDate(): Date | null {
        return this.model.requestedDate;
    }

    @computed
    public get approvalSectionName(): string {
        return this.model.approvalSectionName;
    }

    @computed
    public get approvalTypeName(): string {
        return this.model.approvalTypeName;
    }

    @computed
    public get requestedBy(): string {
        return this.model.requestedBy;
    }

    @computed
    public get requisitionOrPOId(): string | null {
        return this.model.requisitionOrPOId;
    }

    @computed
    public get variationId(): string | null {
        return this.model.variationId;
    }

    @computed
    public get drawingId(): string | null {
        return this.model.drawingId;
    }

    @computed
    public get invoiceId(): string | null {
        return this.model.invoiceId;
    }

    @computed
    public get itemDescription() {
        return this.model.itemDescription;
    }

    @computed
    public get itemCost() {
        return this.model.itemCost;
    }

    @computed
    public get itemCostFormatted() {
        return this.model.itemCost ? formatCurrencyFromPounds(this.model.itemCost) : null;
    }

    @computed
    public get approverRoleName() {
        return this.model.approverRoleName;
    }

    @computed
    public get supplierName() {
        return this.model.supplierName;
    }

    // #endregion Properties

    // #region Actions

    @action
    public navigateToFormApproval = (parentViewModel: ApprovalListViewModel): void => {
        // TODO: Update this to use an enum.
        switch (this.model.approvalSectionName) {
            case "Purchase Requisition":
                this.navigateToRequisitionApproval();
                break;
            case "Variation":
                this.navigateToVariationApproval();
                break;
            case "Client Provisional Sum":
                this.navigateToCPSApproval();
                break;
            case "Invoice":
                this.navigateToInvoiceApproval();
                break;
            case "Material":
                this.navigateToMaterialApproval(parentViewModel);
                break;
            case "Stock":
                this.navigateToStockApproval(parentViewModel);
                break;
            case "Drawing":
                this.navigateToDrawing(parentViewModel);
                break;
            default:
                break;
        }
    };

    @action
    private navigateToRequisitionApproval = () => {
        this.history.push(AppUrls.Client.PurchaseOrder.Edit.replace(":poid", this.requisitionOrPOId!).replace(":approvalMode", "true"));
    };

    @action
    private navigateToVariationApproval = (): void => {
        this.history.push(AppUrls.Client.Variation.Edit.replace(":variationid", this.variationId!).replace(":approvalMode", "true"));
    };

    @action
    private navigateToCPSApproval = (): void => {
        this.history.push(AppUrls.Client.ClientProvisionalSum.Edit.replace(":variationid", this.variationId!).replace(":approvalMode", "true"));
    };

    @action
    private navigateToInvoiceApproval = (): void => {
        this.history.push(AppUrls.Client.Invoicing.Edit.replace(":invoiceid", this.invoiceId!).replace(":approvalMode", "true"));
    };

    // #endregion Actions

    // #region Approval View

    @action
    private navigateToMaterialApproval = (parentViewModel: ApprovalListViewModel): void => {
        parentViewModel.navigateToMaterialApproval(this);
    };

    @action
    private navigateToStockApproval = (parentViewModel: ApprovalListViewModel): void => {
        parentViewModel.navigateToStockApproval(this);
    };

    @action
    private navigateToDrawing = (parentViewModel: ApprovalListViewModel): void => {
        parentViewModel.navigateToDrawing(this);
    };

    // #endregion Approval View

    // #region Viewmodel Boilerplate

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public async isFieldValid(fieldName: keyof FieldType<MyApprovalsNewListItemModel>): Promise<boolean> {
        return true;
    }

    // #endregion Viewmodel Boilerplate
}
